import { useCallback, useEffect, useState } from 'react';

import { Group, Burger, AppShell, Image, em, Avatar, Box, Anchor, SimpleGrid, Center, Menu } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import logo from 'assets/logo-white.svg';
import { EmptyMessage, Loader, Text } from 'components';
import ChangePasswordModal from 'components/modal/change-password';
import { IRootState } from 'config/redux';
import { AppConstants, AuthorityConstants, RouteConstants, ServiceConstants } from 'constant';
import { PublicService, UserService } from 'services';
import classes from 'styles/display.module.css';
import { UserResponse } from 'types';
import { AppUtils, StorageUtils, UserUtils } from 'utils';

import AgentStatusMenu from './AgentStatusMenu';
import LanguageSwitcher from './LanguageSwitcher';


const { STORAGE_USER } = AppConstants;
const {
  ROLE_CC,
  ROLE_CR,
  ROLE_DASH_ADMIN,
  ROLE_USR_REC,
  ROLE_USR,
  ROLE_TUTTI_R,
  ROLE_TUTTI_C,
  ROLE_CHT_R,
  ROLE_CHT_R_MY,
  ROLE_CONTROL,
  ROLE_PRF_CUD,
  ROLE_TODO_R,
  ROLE_TODO_CUD,
  ROLE_TARGET,
  ROLE_HOL_CUD,
  ROLE_MM_R,
  ROLE_MM_CRUD,
} = AuthorityConstants;
const { ROUTE_LOGIN, ROUTE_DASHBOARD, ROUTE_CLIENTS, ROUTE_MY_CLIENTS, ROUTE_REPLACEMENT_CLIENTS, ROUTE_CARDS, ROUTE_USER_RECOMMENDATIONS, ROUTE_USERS, ROUTE_CHAT, ROUTE_CONTROL, ROUTE_TODO, ROUTE_PROFILE, ROUTE_PERMISSIONS, ROUTE_TARGETS, ROUTE_HOLIDAYS, ROUTE_MOTIVATIONAL_MESSAGES } = RouteConstants;
const { REQUEST_USERS_CLIENTS_ABSENCE_AVAILABLE_ID } = ServiceConstants;

interface CustomLayout {
  authority?: string | string[];
  children?: any;
  loading?: boolean;
  title?: string;
  padding?: any;
}

interface RouterProps {
  href?: string;
  title?: string;
  icon?: React.ReactElement;
  children?: RouterProps[];
  active?: boolean;
}

interface NavbarViewProps {
  opened: boolean;
  toggle: () => void;
  logout: () => void;
  routes: RouterProps[];
  t: TFunction;
  user?: UserResponse;
  navigate: (href: string) => void;
  showChangePassword: () => void;
}

interface  UserAvatarProps {
  user?: UserResponse;
}

const NAVIGATION_MOBILE_BREAKPOINT = em(1240);
const DASHBOARD_VIEW_QUERY_KEY = 'ansicht';
const TEAM_LEAD_DASHBOARD_QUERY_VALUE = 'team-lead';
const TEAM_LEAD_DASHBOARD_ROUTE =
  `${ROUTE_DASHBOARD}?${DASHBOARD_VIEW_QUERY_KEY}=${TEAM_LEAD_DASHBOARD_QUERY_VALUE}`;
const DASHBOARD_VARIANT_TITLE_KEYS = {
  ADMIN: 'header.dashboardOptions.admin',
  ADMINISTRATOR: 'header.dashboardOptions.administrator',
  AGENT: 'header.dashboardOptions.agent',
} as const;

const getDashboardRoute = (user?: UserResponse): RouterProps => {
  const primaryDashboardVariant = UserUtils.getDashboardVariant(user);
  const isDashboardActive = window.location.href.includes(ROUTE_DASHBOARD);
  const selectedDashboardView =
    new URLSearchParams(window.location.search).get(DASHBOARD_VIEW_QUERY_KEY);

  if (!user?.isTeamLead || !primaryDashboardVariant) {
    return {
      href: ROUTE_DASHBOARD,
      title: 'header.dashboard',
      active: isDashboardActive,
    };
  }

  return {
    title: 'header.dashboard',
    active: isDashboardActive,
    children: [
      {
        href: ROUTE_DASHBOARD,
        title: DASHBOARD_VARIANT_TITLE_KEYS[primaryDashboardVariant],
        active: isDashboardActive && selectedDashboardView !== TEAM_LEAD_DASHBOARD_QUERY_VALUE,
      },
      {
        href: TEAM_LEAD_DASHBOARD_ROUTE,
        title: 'header.dashboardOptions.teamLead',
        active: isDashboardActive && selectedDashboardView === TEAM_LEAD_DASHBOARD_QUERY_VALUE,
      },
    ],
  };
};

const LayoutStandard = ({ authority, children, loading, title, padding }: CustomLayout) => {
  const [user] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER))
  const [opened, { toggle }] = useDisclosure(false);
  const [routes, setRoutes] = useState<RouterProps[] | undefined>(undefined);
  const [changePasswordVisible, setChangePasswordVisible] = useState<boolean>(false);
  const [canViewReplacementClients, setCanViewReplacementClients] = useState(false);
  const isMobileNavigation = useMediaQuery(`(max-width: ${NAVIGATION_MOBILE_BREAKPOINT})`);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const absenceClientsAvailableServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_ABSENCE_AVAILABLE_ID],
  );

  const hasLayoutAuthority = !authority || (
    Array.isArray(authority)
      ? UserUtils.hasAnyAuthority(authority)
      : UserUtils.hasAuthority(authority)
  );


  useEffect(() => {
    document.title = title ? title.toString() : 'Webkredit.ch';
  }, [title]);

  useEffect(() => {
    if (!UserUtils.hasAuthority(ROLE_CR)) {
      return;
    }
    UserService.userClientsAbsenceAvailable();
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(
      absenceClientsAvailableServiceObject,
      REQUEST_USERS_CLIENTS_ABSENCE_AVAILABLE_ID,
      (value) => setCanViewReplacementClients(Boolean(value)),
      () => setCanViewReplacementClients(false),
    );
  }, [absenceClientsAvailableServiceObject]);

  useEffect(() => {
    if (!user) {
      navigate(RouteConstants.ROUTE_LOGIN);
      return;
    }
    const tempRouter: RouterProps[] = [];
    const canViewTutti = UserUtils.hasAnyAuthority([ROLE_TUTTI_R, ROLE_TUTTI_C]);
    const canViewClients = UserUtils.hasAuthority(ROLE_DASH_ADMIN);
    const canViewMyClients = UserUtils.hasAuthority(ROLE_CR);
    const isConfigurationActive =
      window.location.href.includes(ROUTE_TARGETS) ||
      window.location.href.includes(ROUTE_PERMISSIONS) ||
      window.location.href.includes(ROUTE_HOLIDAYS) ||
      window.location.href.includes(ROUTE_MOTIVATIONAL_MESSAGES);

    tempRouter.push(getDashboardRoute(user))
    if (canViewClients) {
      const clientChildren: RouterProps[] = [
        { href: ROUTE_CLIENTS, title: 'header.clients.all', active: window.location.href.includes(ROUTE_CLIENTS) },
        { href: ROUTE_CLIENTS + '?source=mobile', title: 'header.clients.mobile', active: window.location.href.includes(ROUTE_CLIENTS) },
        { href: ROUTE_CLIENTS + '?source=web', title: 'header.clients.web', active: window.location.href.includes(ROUTE_CLIENTS) },
      ];
      if (canViewTutti) {
        clientChildren.push({
          href: ROUTE_CLIENTS + '?source=tutti',
          title: 'header.clients.tutti',
          active: window.location.href.includes(ROUTE_CLIENTS),
        });
      }
      clientChildren.push({
        href: ROUTE_CLIENTS + '?source=other',
        title: 'header.clients.other',
        active: window.location.href.includes(ROUTE_CLIENTS),
      });

      tempRouter.push({ title: 'header.clients.title', active: window.location.href.includes(ROUTE_CLIENTS), children: [
        ...clientChildren
      ]})
    }
    if (canViewMyClients) {
      const myClientChildren: RouterProps[] = [
        { href: ROUTE_MY_CLIENTS, title: 'header.my_clients.all', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
        { href: ROUTE_MY_CLIENTS + '?source=mobile', title: 'header.my_clients.mobile', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
        { href: ROUTE_MY_CLIENTS + '?source=web', title: 'header.my_clients.web', active: window.location.href.includes(ROUTE_MY_CLIENTS) },
      ];
      if (canViewTutti) {
        myClientChildren.push({
          href: ROUTE_MY_CLIENTS + '?source=tutti',
          title: 'header.my_clients.tutti',
          active: window.location.href.includes(ROUTE_MY_CLIENTS),
        });
      }
      myClientChildren.push({
        href: ROUTE_MY_CLIENTS + '?source=other',
        title: 'header.my_clients.other',
        active: window.location.href.includes(ROUTE_MY_CLIENTS),
      });

      tempRouter.push({ title: 'header.my_clients.title', active: window.location.href.includes(ROUTE_MY_CLIENTS), children: [
        ...myClientChildren
      ]})

      if (canViewReplacementClients) {
        tempRouter.push({
          href: ROUTE_REPLACEMENT_CLIENTS,
          title: 'header.replacement_clients',
          active: window.location.href.includes(ROUTE_REPLACEMENT_CLIENTS),
        });
      }
    }
    if (UserUtils.hasAnyAuthority([ROLE_CHT_R, ROLE_CHT_R_MY])) {
      tempRouter.push({ href: ROUTE_CHAT, title: 'header.chat', active: window.location.href.includes(ROUTE_CHAT) })
    }
    if (UserUtils.hasAuthority(ROLE_USR_REC)) {
      tempRouter.push({ href: ROUTE_USER_RECOMMENDATIONS, title: 'header.recommendations', active: window.location.href.includes(ROUTE_USER_RECOMMENDATIONS) })
    }
    if (UserUtils.hasAuthority(ROLE_CC)) {
      tempRouter.push({ href: ROUTE_CARDS, title: 'header.cards', active: window.location.href.includes(ROUTE_CARDS) })
    }
    if (UserUtils.hasAuthority(ROLE_USR)) {
      tempRouter.push({ href: ROUTE_USERS, title: 'header.users', active: window.location.href.includes(ROUTE_USERS) })
    }
    if (UserUtils.hasAuthority(ROLE_CONTROL) && process.env.REACT_APP_NODE_ENV !== 'production') {
      tempRouter.push({ href: ROUTE_CONTROL, title: 'header.control', active: window.location.href.includes(ROUTE_CONTROL) })
    }
    if (UserUtils.hasAnyAuthority([ROLE_TODO_R, ROLE_TODO_CUD])) {
      tempRouter.push({ href: ROUTE_TODO, title: 'header.todo', active: window.location.href.includes(ROUTE_TODO) })
    }

    const configurationRoutes: RouterProps[] = [];
    if (UserUtils.hasAuthority(ROLE_TARGET)) {
      configurationRoutes.push({
        href: ROUTE_TARGETS,
        title: 'header.targets',
        active: window.location.href.includes(ROUTE_TARGETS),
      });
    }
    if (UserUtils.hasAuthority(ROLE_PRF_CUD)) {
      configurationRoutes.push({
        href: ROUTE_PERMISSIONS,
        title: 'header.permissions',
        active: window.location.href.includes(ROUTE_PERMISSIONS),
      });
    }
    if (UserUtils.hasAuthority(ROLE_HOL_CUD)) {
      configurationRoutes.push({
        href: ROUTE_HOLIDAYS,
        title: 'header.holidays',
        active: window.location.href.includes(ROUTE_HOLIDAYS),
      });
    }
    if (UserUtils.hasAnyAuthority([ROLE_MM_R, ROLE_MM_CRUD])) {
      configurationRoutes.push({
        href: ROUTE_MOTIVATIONAL_MESSAGES,
        title: 'header.motivationalMessage',
        active: window.location.href.includes(ROUTE_MOTIVATIONAL_MESSAGES),
      });
    }
    if (configurationRoutes.length > 0) {
      tempRouter.push({
        title: 'header.configuration',
        active: isConfigurationActive,
        children: configurationRoutes,
      });
    }
    setRoutes(tempRouter);
  }, [canViewReplacementClients, navigate, user]);

  const logout = useCallback(async () => {
    await PublicService.logout();
    navigate(ROUTE_LOGIN);
  }, [navigate]);

  if (!routes || !user) {
    return <></>
  }

  return (
    <AppShell
      layout={'alt'}
      header={{ height: 56 }}
      navbar={{ width: 300, breakpoint: NAVIGATION_MOBILE_BREAKPOINT, collapsed: { desktop: true, mobile: !opened } }}
      padding={padding ?? 'md'}
    >
      <AppShell.Header bg={'#2e2b3c'}>
        <Group h="100%" px="md">
          <ChangePasswordModal opened={changePasswordVisible} onClose={setChangePasswordVisible} t={t} />
          {isMobileNavigation
          ? <RenderMobileView
              opened={opened}
              toggle={toggle}
              logout={logout}
              routes={routes}
              user={user}
              navigate={navigate}
              showChangePassword={() => setChangePasswordVisible(true)}
              t={t} />
          : <RenderWebView
              opened={opened}
              toggle={toggle}
              logout={logout}
              routes={routes}
              navigate={navigate}
              user={user}
              showChangePassword={() => setChangePasswordVisible(true)}
              t={t} />
          }
        </Group>
      </AppShell.Header>
      <AppShell.Main style={{ display: 'flex', flexDirection: 'column' }}>
        {hasLayoutAuthority ?
          loading ?
            <Center h={'100vh'}><Loader /></Center> : <Box style={{ flexGrow: 1 }}>{children}</Box>
          : <EmptyMessage message={t('authority.no')} />
        }
      </AppShell.Main>
    </AppShell>
  )
};

const RenderMobileView = ({ opened, toggle, logout, routes, t, user, navigate, showChangePassword }: NavbarViewProps) => (
  <Group flex={1} justify={'space-between'} align={'center'}>
    <Burger opened={opened} onClick={toggle} size={'sm'} color={'var(--mantine-color-white)'} />
    <AppShell.Navbar bg={'#2e2b3c'}>
      <Burger mx="md" my='md' opened={opened} onClick={toggle} size={'sm'} color={'var(--mantine-color-white)'} />
      <Group flex={1} justify={'space-between'} align={'center'}>
        <SimpleGrid cols={1} verticalSpacing={'xs'}>
          {routes.map((item, key) => (
            item.href ?
              <Anchor key={`drawer-${key}`} onClick={() => item.href && navigate(item.href)} underline={'never'} className={classes.navbarItem}>
                {item.icon}
                <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{item.title ? t(item.title) : undefined}</Text>
              </Anchor>
            : <Menu
                key={`drawer-${key}`}
                shadow={'md'}
                withArrow={true}
                width={200}
                position={'bottom'}
                arrowOffset={10}
                offset={16}
                zIndex={99999}
              >
                <Menu.Target>
                  <Anchor key={`drawer-${key}`} underline={'never'} className={classes.navbarItem}>
                    {item.icon}
                    <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{item.title ? t(item.title) : undefined}</Text>
                    <IconChevronDown size={18} stroke={1.5} color={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} />
                  </Anchor>
                </Menu.Target>
                <Menu.Dropdown>
                  {item.children?.map((child: RouterProps, childIndex: number) => (
                    <Menu.Item
                      key={`item-${childIndex}`}
                      leftSection={child.icon}
                      fs={'lg'}
                      onClick={() => child.href && navigate(child.href)}
                    >
                      {child.title && t(child.title)}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
          ))}
        </SimpleGrid>
      </Group>
    </AppShell.Navbar>
    <Image src={logo} w={40} h={34} style={{ objectFit: 'contain' }} />
    <Group gap={'xs'}>
      <LanguageSwitcher />
      <Menu trigger={'click'} withArrow={true}>
        <Menu.Target>
          <Box ta={'center'} style={{ cursor: 'pointer' }}>
            <UserAvatar user={user} />
          </Box>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate(ROUTE_PROFILE)}>
            {t('profile')}
          </Menu.Item>
          <Menu.Item onClick={showChangePassword}>
            {t('password.change.title')}
          </Menu.Item>
          <Menu.Item onClick={logout} color={'var(--mantine-color-error)'}>
            {t('logout')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  </Group>
)

const RenderWebView = ({ navigate, logout, routes, user, t, showChangePassword }: NavbarViewProps) => (
  <Group flex={1} justify={'space-between'} align={'center'}>
    <Group gap={10}>
      <Image src={logo} w={40} h={34} style={{ objectFit: 'contain' }} />
      <Group gap={4}>
        {routes.map((item, key) => (
          item.href ?
            <Anchor key={`drawer-${key}`} onClick={() => item.href && navigate(item.href)} underline={'never'} className={classes.navbarItem}>
              {item.icon}
              <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'sm'}>{item.title ? t(item.title) : undefined}</Text>
            </Anchor>
          :
          <Menu
            key={`drawer-${key}`}
            shadow={'md'}
            withArrow={true}
            width={200}
            position={'bottom'}
            arrowOffset={10}
            offset={16}
            zIndex={99999}
          >
            <Menu.Target>
              <Anchor underline={'never'} className={classes.navbarItem}>
                {item.icon}
                <Text c={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} size={'md'}>{item.title ? t(item.title) : undefined}</Text>
                <IconChevronDown size={18} stroke={1.5} color={item.active ? 'var(--mantine-color-primary-6)' : 'var(--mantine-color-white)'} />
              </Anchor>
            </Menu.Target>
            <Menu.Dropdown>
              {item.children?.map((child: RouterProps, childIndex: number) => (
                <Menu.Item
                  key={`item-${childIndex}`}
                  leftSection={child.icon}
                  fs={'lg'}
                  onClick={() => child.href && navigate(child.href)}
                >
                  {child.title && t(child.title)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        ))}
      </Group>
    </Group>
    <Group gap={'sm'}>
      <LanguageSwitcher />
      {user && UserUtils.isAgentDashboardUser(user)
        ? <AgentStatusMenu user={user} />
        : <Text c={'var(--mantine-color-white)'} size={'md'}>{user?.fullName}</Text>
      }
      <Menu trigger={'click'} withArrow={true}>
        <Menu.Target>
          <Box mb={6} ta={'center'} style={{ cursor: 'pointer' }}>
            <UserAvatar user={user} />
          </Box>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={() => navigate(ROUTE_PROFILE)}>
            {t('profile')}
          </Menu.Item>
          <Menu.Item onClick={showChangePassword}>
            {t('password.change.title')}
          </Menu.Item>
          <Menu.Item onClick={logout} color={'var(--mantine-color-error)'}>
            {t('logout')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  </Group>
)

const UserAvatar = ({ user }: UserAvatarProps) => {
  let imageUrl = null;
  if (user?.profileImage) {
    imageUrl = `${process.env.REACT_APP_API_URL_ATTACHMENTS}/${user.profileImage}`;
  }
  return <Avatar size={32} src={imageUrl} radius={'xl'} className={classes.initials} />
}
export default LayoutStandard;
